// --- BETTING MATH & STATUS — pure functions, no React dependencies ------------
import { isMoneylineMarket, isStatFallbackMarket, marketKey, isBinaryMarket,
         isEnrichablePlayerProp, isPlayerPropMarket } from './markets';
import { SETTLEABLE_LEAGUES, SUPPORTED_LEAGUES, GRADABLE_MARKETS_BY_LEAGUE } from './constants';
import { resolveLeagueAuthority, normalizeLeagueSignal } from './league-authority';

// --- PARLAY MATH --------------------------------------------------------------
export function toDecimal(american) {
  const n = Number(american);
  if (isNaN(n)||n===0) return 1;
  return n > 0 ? (n/100)+1 : (100/Math.abs(n))+1;
}
export function toAmerican(decimal) {
  if (decimal <= 1) return null;
  if (decimal >= 2) return `+${Math.round((decimal-1)*100)}`;
  return `${Math.round(-100/(decimal-1))}`;
}
export function parlayOdds(legs) {
  if (!legs.length) return null;
  const combined = legs.reduce((acc, leg) => acc * toDecimal(leg.odds), 1);
  return toAmerican(combined);
}
export function calcPayout(stake, odds) {
  const n = Number(odds);
  if (isNaN(n)||n===0) return "0.00";
  const dec = toDecimal(n);
  const p = stake * (dec - 1);
  // #878 S1: a non-finite (overflow/Infinity) odds value must not fabricate an
  // Infinity payout that propagates into displayed P&L.
  return Number.isFinite(p) ? p.toFixed(2) : "0.00";
}
export function fmtOdds(odds) {
  if (odds === null || odds === undefined || odds === "") return "—";
  const s = String(odds);
  if (s.startsWith("+") || s.startsWith("-")) return s;
  const n = Number(s);
  if (isNaN(n)) return s;
  return n > 0 ? `+${s}` : s;
}

// --- PLAYER MATCHING ----------------------------------------------------------
export function normalizeStr(s) {
  return s.trim().toLowerCase()
    .replace(/ü/g, "ue").replace(/ö/g, "oe").replace(/ä/g, "ae")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// #1040 PR C edit 7 -- generational suffixes. ~10% of ESPN NFL athlete names end in
// Jr. / Sr. / II / III (6 of 69 in the Week-1 fixture); sportsbooks print the bare
// name, so "Luther Burden" never matched "Luther Burden III" and the leg was a
// permanent stat-miss. Strip ONE trailing suffix token from BOTH sides, only when at
// least two tokens remain (a two-token "Ken Jr." keeps its surname). League-agnostic.
// Direction of failure: findPlayerMatch returns null on >1 match, so a father/son pair
// on one box score is a stat-miss (pending), never a mis-grade. Stage-2 tightening is
// intended: "V. Guerrero Jr." vs "Vince Garcia Jr." no longer matches on a shared "jr."
// surname. G9(a): when BOTH raw names carry a suffix and the suffixes differ (Jr vs Sr,
// II vs III -- compared lowercase, period-stripped) they are different people: false
// BEFORE stripping, so a suffixed query against a father/son pair stays unique.
const GENERATIONAL_SUFFIX = /^(jr|sr|ii|iii|iv|v)\.?$/i;
// The suffix token of a name (normalised, no period) or null; only a 3+-token name has one.
function generationalSuffixOf(parts) {
  if (parts.length < 3) return null;
  const last = parts[parts.length - 1];
  return GENERATIONAL_SUFFIX.test(last) ? last.replace(/\.$/, '') : null;
}
function stripGenerationalSuffix(parts) {
  return generationalSuffixOf(parts) ? parts.slice(0, -1) : parts;
}

// #1574 -- a plain accent-strip fold (ü -> u), with no umlaut transliteration.
// normalizeStr maps ü/ö/ä to ue/oe/ae, so an ASCII book name ("Tim Stutzle") folds to
// `stutzle` while the box score ("T. Stützle") folds to `stuetzle`, and they never
// matched. Server grading passes the book's leg.player_name, so those legs stat-missed.
// matchPlayer tries normalizeStr first (keeps "Mueller" vs "Müller"), then this fold.
// Direction of failure: if both folds now match two box-score players, findPlayerMatch
// returns null (pending), never a mis-grade.
function stripAccents(s) {
  return s.trim().toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function matchPlayer(fullName, apiName, legPlayerId, apiPlayerId) {
  if (legPlayerId != null && apiPlayerId != null &&
      typeof legPlayerId === "number" && typeof apiPlayerId === "number") {
    return legPlayerId === apiPlayerId;
  }
  if (!fullName || !apiName) return false;
  if (matchFolded(normalizeStr(fullName), normalizeStr(apiName))) return true;
  return matchFolded(stripAccents(fullName), stripAccents(apiName));
}

function matchFolded(fullFolded, apiFolded) {
  const fullRaw = fullFolded.split(" ");
  const apiRaw  = apiFolded.split(" ");
  const fullSuffix = generationalSuffixOf(fullRaw);
  const apiSuffix  = generationalSuffixOf(apiRaw);
  if (fullSuffix && apiSuffix && fullSuffix !== apiSuffix) return false; // G9(a)
  const fullParts = stripGenerationalSuffix(fullRaw);
  const apiParts  = stripGenerationalSuffix(apiRaw);
  const full = fullParts.join(" ");
  const api  = apiParts.join(" ");
  if (full === api) return true;
  if (fullParts.length < 2 || apiParts.length < 2) return false;
  const fullLast = fullParts[fullParts.length - 1];
  const apiLast  = apiParts[apiParts.length - 1];
  const fullInit = fullParts[0][0];
  const apiInit  = apiParts[0].replace(".","")[0];
  if (fullLast === apiLast && fullInit === apiInit) return true;
  const fullFirst = fullParts[0];
  const apiFirst  = apiParts[0].replace(".","");
  const shorter = fullFirst.length <= apiFirst.length ? fullFirst : apiFirst;
  if (shorter.length >= 3 && fullLast === apiLast &&
      (fullFirst.startsWith(apiFirst) || apiFirst.startsWith(fullFirst))) return true;
  return false;
}

export function findPlayerMatch(players, fullName, legPlayerId) {
  if (!players?.length) return null;
  const legId = typeof legPlayerId === "number" ? legPlayerId : null;
  if (legId != null) {
    const idMatch = players.find(p => typeof p.id === "number" && p.id === legId);
    if (idMatch) return idMatch;
  }
  if (!fullName) return null;
  const matches = players.filter(p => matchPlayer(fullName, p.name));
  if (matches.length === 1) return matches[0];
  return null;
}

// --- BET STATUS ---------------------------------------------------------------

export function hasLineValue(line) {
  // #773: present only if it coerces to a finite number. Rejects null/undefined/""
  // AND non-numeric shapes ({}, "abc", multi-element arrays -> NaN) that would
  // otherwise reach Number(line) and fabricate a result. 0 stays valid (finite).
  // Pre-Codex R2 P2 hardening.
  return line !== null && line !== undefined && line !== "" && Number.isFinite(Number(line));
}

// #1135: an Over/Under line is a count (goals, runs, points, shots...), never negative.
// `hasLineValue` alone accepts -44.5, and `total > -44.5` makes Over a guaranteed win
// (Under a guaranteed loss), terminal at FINAL. Only a spread-family line is signed --
// its sign IS the bet -- and those branches never call this. 0 stays valid.
export function isNegativeLine(line) {
  return hasLineValue(line) && Number(line) < 0;
}

// #789 fail-closed pick guard. Bare === (not String()): pick and team
// abbreviations are definitionally strings at every layer (import, storage,
// grading) — no integer-coercion path exists, so CC-IMPL-001 (a game-ID-specific
// rule) does not apply here. Identical comparison semantics to each team-pick
// branch's own `pick === game.home.team` keeps the safety invariant a one-line
// audit rather than an argument. Optional chaining is null-safety for direct
// unit tests only; `game` is guaranteed non-null past calcStatus's line-131 guard.
export function pickMatchesTeam(pick, game) {
  return pick === game?.home?.team || pick === game?.away?.team;
}

// #773: markets graded off a numeric line via score math (Number(line)). A null
// line here would compute Number(null)=0 and fabricate a won/lost. These must go
// unresolvable instead. This is calcStatus market-key vocabulary (spec §5) — NOT
// the markets.js isLineRequired() predicate, which is broader and covers
// player-prop markets already guarded by the liveStatVal branch below.
// Will be kept in sync with the forthcoming SlipSync import gate (#773 §3b,
// api/lib/slipsync/handler.js) — CJS cannot import this ESM const. That gate is a
// separate PR (773b); update both together once it lands.
// #1040 PR D: nfl:spread / nfl:total join -- api/lib/slipsync/handler.js mirrors this Set (parity-pinned).
// #1655 B1 (Ultron P1-2): nba:spread / nba:total join for the SlipSync null-line gate
// (the handler mirror is parity-pinned to this Set). Inert in calcStatus until D1: nba
// is not in SETTLEABLE_LEAGUES, so calcStatus returns before this Set is read; the only
// other reader, ungradeableLegCause, is a forensic label for legs settlement processes,
// and settlement never processes an nba leg before D1.
export const LINE_REQUIRED_MARKETS = new Set(['spread', 'over_under', 'ou', 'pl', 'nfl:spread', 'nfl:total', 'nba:spread', 'nba:total']);

// #1138 P2-1: the markets each league's poller can still freeze -- a client-importable
// MIRROR of every polling adapter's recoverySweepMarkets (api/lib/adapters/*-polling-
// adapter.js; src/lib cannot import api/). Pinned equal at runtime by
// freeze-eligible-markets-drift.test.js, so an adapter change fails CI until this
// mirror moves with it (CC-IMPL-026 owns the adapter lists themselves). It exists
// because isEnrichablePlayerProp alone misses the bare MLB ids (`hits`, `rbis`, ...):
// the poller freezes them, so an unfrozen one is "not yet", never terminal.
export const FREEZE_ELIGIBLE_MARKETS_BY_LEAGUE = Object.freeze({
  nhl: Object.freeze(new Set([
    'sog', 'nhl:shots_on_goal',
    'goals', 'nhl:goals', 'anytime_goal',
    'assists', 'nhl:assists', 'anytime_assist', 'nhl:anytime_assist',
    'points', 'nhl:points', 'anytime_point',
  ])),
  mlb: Object.freeze(new Set([
    'mlb:hits', 'hits',
    'mlb:home_runs', 'home_runs',
    'mlb:rbis', 'rbis',
    'mlb:runs', 'runs',
    'mlb:total_bases', 'total_bases',
    'mlb:strikeouts_pitcher', 'strikeouts_pitcher',
    'mlb:strikeouts_batter', 'strikeouts_batter',
  ])),
  nfl: Object.freeze(new Set([
    'nfl:passing_yards', 'nfl:rushing_yards', 'nfl:receiving_yards', 'nfl:receptions', 'nfl:anytime_td',
  ])),
});

/**
 * canReceiveFrozenStat -- can a leg on this market still be given a frozen_stat?
 *
 * Single job (#1138 P2-1): answer "enrichable player prop OR in the league's freeze
 * list". The ONE answer read by isTerminallyUngradeableLeg (client and server) and by
 * the settlement processor's ambiguous_frozen_stat holds (multi and single), so the
 * terminal rule and the hold cannot disagree. Pure; reads no leg state.
 */
export function canReceiveFrozenStat(market, league) {
  if (!market || league == null) return false;
  const key = String(league).trim().toLowerCase();
  if (isEnrichablePlayerProp(market, key)) return true;
  if (!Object.prototype.hasOwnProperty.call(FREEZE_ELIGIBLE_MARKETS_BY_LEAGUE, key)) return false;
  return FREEZE_ELIGIBLE_MARKETS_BY_LEAGUE[key].has(market);
}

/**
 * readsPlayerStat -- does this leg's progress come from a player's stat?
 *
 * Single job (#1683): the display question "show this leg's stat, count it toward hit/bust".
 * isPlayerPropMarket() alone misses nhl:goals (anytime goal scorer, playerProp:false -- the
 * flag other surfaces rely on, so it stays) and the bare MLB ids; canReceiveFrozenStat() is
 * the shared "this market gets a player stat" answer and covers both. Team markets match
 * neither. Display only: no settlement path reads it.
 */
export function readsPlayerStat(leg, league) {
  if (isPlayerPropMarket(leg)) return true;
  const market = marketKey(leg);
  if (canReceiveFrozenStat(market, league)) return true;
  // Pre-Codex F1: the market's own prefix still names it when no grading league resolves (a null
  // root, a leg/root mismatch). The leg is then a player leg with no readable stat, which every
  // caller fails closed on -- never a team leg that counts as hit.
  const own = typeof market === 'string' && market.includes(':') ? market.split(':')[0] : null;
  return own != null && canReceiveFrozenStat(market, own);
}

// Project A (never-reject capture): a leg the SlipSync import could not resolve is
// stored with tracking_status='not_tracked'. It can never be graded until Project B
// backfills it -- its own status is always 'pending' (non-settleable).
export function isUntrackedLeg(leg) {
  return leg?.trackingStatus === 'not_tracked';
}

// #1744 (Nicky 2026-10-26, D1): true when a leg's stored `pick` is the bare word "No" -- the
// user took the NO side of a binary player prop. Reads `pick` only: the server leg shapes carry
// no raw selection text, so reading it here would grade the client apart from the cron. Trims
// Unicode whitespace (NBSP, zero-width, BOM included) and ignores case; any other text is not No.
// Callers gate on the leg being binary first (calcStatus's binary branch; the SlipTicket and
// ResultsPage display spots via isBinaryPlayerProp) -- this reads the side, not the market.
// A leg with a line is false: calcStatus grades it on the line branch, so the display spots
// must not mirror it either (pre-Codex F1).
export function isNoBinaryLeg(leg) {
  const pick = leg?.pick;
  if (typeof pick !== 'string' || hasLineValue(leg.line)) return false;
  return pick.replace(/^[\s\u200b\ufeff]+|[\s\u200b\ufeff]+$/g, '').toLowerCase() === 'no';
}

function hasExplicitBinarySignal(bet, market, league) {
  // Single source of truth: derive binary classification from the market registry.
  // Covers anytime_goal, anytime_point, anytime_assist, nhl:anytime_assist,
  // and all markets with binary: true in MARKET_DEFS.
  if (isBinaryMarket(market, league)) {
    return true;
  }
  // mlb:strikeouts_batter is O/U by market definition (binary: false,
  // requiresLineInput: true) but DraftKings offers it as a threshold bet
  // ("To Record 1+ Strikeouts") with no fractional line on the slip.
  // Only "1+" is truly binary (val > 0). Higher thresholds ("2+", "3+")
  // cannot use the binary branch because val > 0 mis-grades them --
  // e.g. pick "2+" with stat 1 would grade "won" instead of "lost".
  // Those fall through to the isEnrichablePlayerProp guard → unresolvable.
  if (market === 'mlb:strikeouts_batter') {
    return /^1\+$/.test(String(bet?.pick || '').trim());
  }
  // Raw text fallback for sportsbook strings not yet captured by market aliases.
  const rawText = [
    bet?.marketTextRaw,
    bet?.market,
    bet?.type,
    bet?.pick,
  ].filter(Boolean).join(" ").toLowerCase();
  return /\banytime\b|\bto score\b|\bgoal scorer\b|\bgoalscorer\b|\bto record (?:a|an) (?:point|assist)\b/
    .test(rawText);
}

// #1353 PR 3b (spec §3 betting.js row, §9 DNP precedence). bet_legs.frozen_dnp is written only by
// the server freeze after its own finality check, and clients cannot write it (trigger). A
// client-authored JSONB leg MAY carry a frozenDnp key (import-confirm passes arbitrary keys), so
// both read paths strip it (parseLegs on the server, legFromJsonbFailClosed on the client). The flag is honoured only
// where grading would honour the leg at all: the league authority and market admission are
// re-applied here, so a flag never overrides a refusal (r5b.1 P3).
function dnpAdmitted(leg, league) {
  if (league == null || String(league).trim() === '') return false;
  const key = String(league).trim().toLowerCase();
  if (!SETTLEABLE_LEAGUES.includes(key)) return false;
  const market = marketKey(leg);
  const gradable = GRADABLE_MARKETS_BY_LEAGUE[key];
  if (gradable && !gradable.has(market)) return false;
  return isEnrichablePlayerProp(market, key);
}

// Server rule: the flag + a FINAL/OFF game + an admitted enrichable player prop.
export function isFrozenDnpLeg(leg, game, league) {
  return leg?.frozenDnp === true
    && (game?.status === 'FINAL' || game?.status === 'OFF')
    && dnpAdmitted(leg, league);
}

// Client display rule (r5b.4 I1): the flag is itself the finality evidence, so no game check --
// a mounted ticket whose cached game is LIVE/UPCOMING/absent (game_cache is unpublished) still
// shows DNP. Only callers that pass { trustDnpFlag: true } reach it; no api/ file does.
export function isDisplayDnpLeg(leg, league) {
  return leg?.frozenDnp === true && dnpAdmitted(leg, league);
}

// #790: `allowPush` opts an exact-line FINAL outcome into a `push` (stake refund)
// instead of a loss (or, for a true-O/U prop, a false win). Default false keeps
// every parlay/live/client caller byte-identical; only the single-bet settle path
// (settlement-processor single grade + settle-slips single-parent recovery) opts in.
export function calcStatus(bet, game, league, allowPush = false, opts = {}) {
  if (isUntrackedLeg(bet)) return 'pending';
  // #1353 PR 3b client display (trustDnpFlag, r5b.4 I1): before the game-status guard.
  if (opts?.trustDnpFlag === true && isDisplayDnpLeg(bet, league)) return 'push';
  if (!game || game.status === "UPCOMING" || game.status === "POSTPONED" || game.status === "DELAYED") return "pending";
  if (league == null || String(league).trim() === '') {
    return 'unresolvable';
  }

  // STRUCTURAL SETTLEMENT GATE (#1037, GPT audit P1-1).
  //
  // A league with no settlement path must never display a verdict. Everything below
  // this line grades -- including the `liveStatVal` branch, which consults NEITHER
  // league NOR market capability -- so the gate has to sit above all of it.
  //
  // Placed BEFORE LINE_REQUIRED_MARKETS and every market branch on purpose. The
  // previous guard was that no NFL stat writer exists, which is a circumstance
  // rather than a boundary, and there WAS a live route around it (closed by #1052):
  // a null-market NFL leg made rowToBet reject the whole normalized bet_legs set and
  // fall back to the client-controlled bets.legs JSONB, which carried
  // frozenStat/liveStatVal/statValue verbatim. rowToBet no longer reads that copy
  // when rows exist, but this gate stays: it returns before any branch can read a
  // stat regardless of where the leg came from.
  //
  // Keyed on SETTLEABLE_LEAGUES, not marketSupportStatus() -- see that constant for
  // why the market-support version would break NHL `points` on live user data.
  const leagueKey = String(league).trim().toLowerCase();
  if (!SETTLEABLE_LEAGUES.includes(leagueKey)) {
    return (game.status === 'FINAL' || game.status === 'OFF') ? 'unresolvable' : 'pending';
  }

  const market = marketKey(bet);
  const diff = game.home.score - game.away.score;
  const total = game.home.score + game.away.score;
  const fin = game.status === "FINAL" || game.status === "OFF";

  // MARKET ADMISSION (#1040 PR C, GPT round-1 F3/G3, round-2 F1/G5, round-3 F1/G8).
  // What this checks and why: GRADABLE_MARKETS_BY_LEAGUE (constants.js).
  //
  // Placed HERE (after market/diff/total/fin) because those are declared right above --
  // an admission check above them throws ReferenceError on every NFL call (G8). Placed
  // BEFORE every grading branch so a non-member market returns before any of them reads
  // a score, line, or stat. Uses leagueKey (G5, normalised) -- already proven a
  // SETTLEABLE_LEAGUES member here, so the lookup is bounded.
  const gradable = GRADABLE_MARKETS_BY_LEAGUE[leagueKey];
  if (gradable && !gradable.has(market)) {
    return fin ? "unresolvable" : "pending";
  }

  // #1353 PR 3b (§9 step 3): a DNP-frozen admitted prop on a FINAL/OFF game is void at the
  // book -> push, before every market branch and independent of allowPush (which governs
  // exact-line pushes only). Never above the league/admission gates.
  if (isFrozenDnpLeg(bet, game, leagueKey)) return 'push';

  // #773 null-line guard — see LINE_REQUIRED_MARKETS. line=0 is valid (hasLineValue
  // true) and passes through to the existing branches; only missing lines are blocked.
  if (LINE_REQUIRED_MARKETS.has(market) && !hasLineValue(bet.line)) {
    return fin ? "unresolvable" : "pending";
  }

  if (isMoneylineMarket(market, league)) {
    if (!pickMatchesTeam(bet.pick, game)) return fin ? "unresolvable" : "pending";
    const lead = diff>0 ? game.home.team : diff<0 ? game.away.team : null;
    if (fin && allowPush && diff === 0) return "push"; // #790: regulation tie refunds
    const w = lead === bet.pick;
    return fin ? (w?"won":"lost") : (!lead?"pending":w?"winning":"losing");
  }

  if (market === "spread" || market === "nfl:spread") { // #1040 PR D: NFL reaches this only via GRADABLE_MARKETS_BY_LEAGUE
    if (!pickMatchesTeam(bet.pick, game)) return fin ? "unresolvable" : "pending";
    const line = Number(bet.line);
    const fav = bet.pick === game.home.team;
    // #790: exact cover is branch-asymmetric — home pick pushes at diff===-line,
    // away pick at diff===line (mirrors each branch's own strict cover test).
    if (fin && allowPush && (fav ? diff === -line : diff === line)) return "push";
    const cover = fav ? diff > -line : diff < line;
    return fin ? (cover?"won":"lost") : (cover?"winning":"losing");
  }

  if (market === "pl") {
    if (!pickMatchesTeam(bet.pick, game)) return fin ? "unresolvable" : "pending";
    const line = hasLineValue(bet.line) && Number(bet.line) !== 0
      ? Number(bet.line) : -1.5;
    const fav = bet.pick === game.home.team;
    // #790: same branch-asymmetric exact-line push as spread. Default -1.5 is a
    // half-line, so an integer `diff` never equals it — default pl cannot push.
    if (fin && allowPush && (fav ? diff === -line : diff === line)) return "push";
    const cover = fav ? diff > -line : diff < line;
    return fin ? (cover?"won":"lost") : (cover?"winning":"losing");
  }

  if (["ou", "over_under", "nfl:total"].includes(market)) { // #1040 PR D
    const ou = bet.overUnder || bet.pick;
    if (ou !== "Over" && ou !== "Under") return fin ? "unresolvable" : "pending";
    if (isNegativeLine(bet.line)) return fin ? "unresolvable" : "pending"; // #1135
    // #790: total exactly on the line refunds (symmetric; scores are integers so
    // this can only hold for an integer line — half-lines never push).
    if (fin && allowPush && total === Number(bet.line)) return "push";
    const hit = ou === "Over" ? total > Number(bet.line) : total < Number(bet.line);
    return fin ? (hit?"won":"lost") : (hit?"winning":"losing");
  }

  if (market === "ftts") {
    // #1119 A2-D5: the first-goal team comes from the server lift or the
    // cron-written liveContext. A value that is not one of THIS game's two teams
    // (another game's first goal, a foreign code) stays pending -- never a grade
    // (N-A2-1, ratified 2026-09-23). Order: no value, value not a team, then pick.
    const ftts = legFirstTeamToScore(bet);
    if (!ftts) return "pending";
    if (ftts !== game?.home?.team && ftts !== game?.away?.team) return "pending";
    if (!pickMatchesTeam(bet.pick, game)) return fin ? "unresolvable" : "pending";
    const w = ftts === bet.pick;
    return fin ? (w?"won":"lost") : (w?"winning":"losing");
  }

  if (bet.liveStatVal !== undefined && bet.liveStatVal !== null) {
    const val = bet.liveStatVal;
    const linePresent = hasLineValue(bet.line);
    const explicitBinary = hasExplicitBinarySignal(bet, market, league);

    if (explicitBinary && !linePresent) {
      // #1744 (D1/D2): a No answer is the mirror -- it loses once the stat moves, and wins
      // only at FINAL with 0. Live with 0 stays pending (the player can still score).
      if (isNoBinaryLeg(bet)) {
        return fin ? (val>0?"lost":"won") : (val>0?"losing":"pending");
      }
      return fin ? (val>0?"won":"lost") : (val>0?"winning":"pending");
    }

    if (linePresent) {
      if (!bet.overUnder) return fin ? "unresolvable" : "pending";
      if (isNegativeLine(bet.line)) return fin ? "unresolvable" : "pending"; // #1135
      const line = Number(bet.line);
      const over = bet.overUnder === "Over" ||
        String(bet.overUnder).endsWith("+");
      // #790: a true O/U prop (Over/Under, integer line, NOT a threshold) refunds
      // when the stat lands exactly on the line. explicitBinary fails closed to
      // threshold — an "X+" or binary-signalled prop keeps inclusive >= and stays
      // won on the line, never pushes.
      const trueOU = !explicitBinary &&
        (bet.overUnder === "Over" || bet.overUnder === "Under") &&
        Number.isInteger(line);
      if (fin && allowPush && trueOU && val === line) return "push";
      const hit = over ? val>=line : val<=line;
      // #1679: only an Over can be one short. An Under that is not hit is already past its line:
      // losing, not close -- counting stats never come back down, and the SGP bust rule
      // (anyLegBusted) already treats it as busted (a yardage stat can dip on a negative play;
      // the same call). Live branch only (!fin).
      const close = over && val>=line-1;
      return fin ? (hit?"won":"lost") : (hit?"winning":close?"close":"losing");
    }

    if (isStatFallbackMarket(market, league)) {
      return fin ? "unresolvable" : "pending";
    }

    if (isEnrichablePlayerProp(market, league) && !linePresent) {
      return fin ? "unresolvable" : "pending";
    }
  }

  // #699 G1 — fail-closed fallthrough. Any leg that matched no market branch above
  // (unknown/unsupported market, or a stat-bearing leg with no gradeable contract) is
  // ungradeable. At a FINAL/OFF game that is terminal, so fail closed to `unresolvable`
  // (visible MISSING DATA) instead of a silent `pending` that never settles. Pre-terminal
  // stays `pending`. Mirrors the existing `fin ? "unresolvable" : "pending"` idiom at the
  // #773/#790/#791 guards above. Scope: settlement safety net only — G2/G3 import gates
  // and the market-support predicate are separate (#699 §0.5). In a parlay, a leg that is
  // unresolvable here never makes the slip a false loss; since #1138 a FINAL parlay whose
  // every other leg won settles terminal `unresolvable` when this leg is statically
  // ungradeable (isTerminallyUngradeableLeg), and stays `pending` otherwise.
  return fin ? "unresolvable" : "pending";
}

const isFinalGame = (g) => g?.status === "FINAL" || g?.status === "OFF";

/**
 * isTerminallyUngradeableLeg -- is this `unresolvable` leg ungradeable for good?
 *
 * Single job (#1138): true only for a leg no later data can grade -- brief rows 1-7
 * (unknown / unsupported / null market, missing or negative line, pick in neither team,
 * no Over/Under side, a frozen prop with no contract). Every condition that can still
 * change keeps it false: a LIVE or absent game, no gameId, an unsettleable league, an
 * unadmitted market (grades the day it is admitted), and a leg the poller can still
 * freeze (canReceiveFrozenStat with frozenStat == null -- `==` so the server's absent
 * key and the client's null both count as unfrozen). Shared by the client and the
 * settlement processor through parlayStatus, so the #791 divergence cannot reopen.
 * `game` is the leg's OWN game (leg.gameId only, as rawParlayLegStatuses resolves it).
 */
export function isTerminallyUngradeableLeg(leg, status, game, league) {
  if (status !== "unresolvable") return false;
  // Redundant (an untracked leg grades `pending`); kept as defence in depth (P2-3).
  if (isUntrackedLeg(leg)) return false;
  if (!leg?.gameId || !isFinalGame(game)) return false;
  if (league == null || String(league).trim() === "") return false;
  const key = String(league).trim().toLowerCase();
  if (!SETTLEABLE_LEAGUES.includes(key)) return false;
  const market = marketKey(leg);
  const gradable = GRADABLE_MARKETS_BY_LEAGUE[key];
  // P2-2: a null/empty market is admitted (G13), as isSettlementPendingForBet and the processor.
  if (gradable && !(!market || gradable.has(market))) return false;
  if (canReceiveFrozenStat(market, key) && leg.frozenStat == null && !isFrozenDnpLeg(leg, game, key)) return false;
  return true;
}

/**
 * ungradeableLegCause -- why a terminally ungradeable leg could not be graded.
 *
 * Single job (#1138 P2-10): a forensic label for settlement_decisions.leg_stats, so a
 * terminal parlay is findable if a later code change would have graded its leg. Mirrors
 * calcStatus's fail-closed branches in order; never used to grade.
 * Returns one of: null_market, no_line, pick_not_in_game, no_side, negative_line,
 * unknown_market (the G1 fallthrough and anything else).
 */
export function ungradeableLegCause(leg, game, league) {
  const market = marketKey(leg);
  if (!market) return "null_market";
  if (LINE_REQUIRED_MARKETS.has(market) && !hasLineValue(leg.line)) return "no_line";
  const teamPick = isMoneylineMarket(market, league) || market === "spread" || market === "nfl:spread"
    || market === "pl" || market === "ftts";
  if (teamPick && !pickMatchesTeam(leg.pick, game)) return "pick_not_in_game";
  if (["ou", "over_under", "nfl:total"].includes(market)) {
    const ou = leg.overUnder || leg.pick;
    if (ou !== "Over" && ou !== "Under") return "no_side";
    if (isNegativeLine(leg.line)) return "negative_line";
  }
  const val = leg.frozenStat ?? leg.liveStatVal ?? null;
  if (val !== null && val !== undefined) {
    if (hasLineValue(leg.line)) {
      if (!leg.overUnder) return "no_side";
      if (isNegativeLine(leg.line)) return "negative_line";
    } else if (isStatFallbackMarket(market, league) || isEnrichablePlayerProp(market, league)) {
      return "no_line";
    }
  }
  return "unknown_market";
}

/**
 * legFirstTeamToScore -- the first-goal team a leg carries, or null (#1119 A2-D5).
 * Single job: read it from the server lift (`firstTeamToScore`) or the cron-written
 * `liveContext.firstTeamToScore`, accepting only a 2-4 capital team code. Server leg
 * shapes never carry liveContext, so on the server this is the fttsMap lift only.
 */
export function legFirstTeamToScore(leg) {
  const v = leg?.firstTeamToScore ?? leg?.liveContext?.firstTeamToScore ?? null;
  return typeof v === "string" && /^[A-Z]{2,4}$/.test(v) ? v : null;
}

/**
 * #699 G1 — client-side single-bet hydration for the display/grading path.
 * A single bet's root (from rowToBet) carries no market/liveStatVal — those live under
 * legs[0] — so grading the market-less root on the display path hits the G1 fallthrough and
 * would show MISSING DATA in the post-FINAL/pre-cron-settlement window. Lift the sole leg's
 * market/stat/pick onto the root so callers grade the real leg. Non-singles (and
 * empty/legless singles) pass through unchanged. Mirrors the server's `hydrateSingleLeg`
 * (`api/lib/settlement-processor.js`): same field set (canonicalMarket, market, playerName,
 * liveStatVal, pick, firstTeamToScore -- the latter only from the graded game, #1119
 * A2-D6; the client also reads the cron-written liveContext, the server only its
 * live_stats lift); it deliberately does NOT lift `line`/`overUnder`, which
 * the root already carries (rowToBet), so client display grades against the same line the
 * server settles against (no client/server divergence). Kept as a separate client copy to
 * avoid touching the high-risk settlement-processor — a future dedupe is out of #699 G1 scope.
 *
 * `liveStatVal` is game-aware (pass the leg's game):
 *  - TERMINAL (FINAL/OFF): frozenStat only — an explicit `frozenStat: null` (#791 frozen-but-
 *    missing) stays null (→ unresolvable, matching the server's settlement; NO `statValue`
 *    divergence — Codex R2). A real 0 is preserved.
 *  - PRE-TERMINAL (LIVE etc.): fall back to the cron live stat (`bet_legs.statValue`, AGENTS.md
 *    "bet_legs is source of truth") so live single props show winning/close/losing instead of
 *    stalling at PENDING (Codex R3). No settlement exists pre-terminal, so nothing to diverge from.
 *  - `game` omitted → treated as pre-terminal (live) — safe default; callers pass the game.
 */
export function hydrateSingleLeg(bet, game) {
  if (bet?.slipType !== 'single' || !bet.legs?.length) return bet;
  const leg = bet.legs[0];
  if (!leg) return bet;
  const legMarket = leg.canonicalMarket ?? leg.market ?? leg.type ?? null;
  const terminal = game?.status === 'FINAL' || game?.status === 'OFF';
  const liveStatVal = terminal
    ? (leg.frozenStat !== null && leg.frozenStat !== undefined ? leg.frozenStat : null)
    : (leg.frozenStat ?? leg.statValue ?? null);
  return {
    ...bet,
    canonicalMarket: legMarket ?? bet.canonicalMarket,
    market: legMarket ?? bet.market,
    playerName: leg.playerName ?? leg.player ?? bet.playerName,
    liveStatVal,
    // #1744 Ultron U1: `||` as the server does (betLegToLegShape `bl.pick || null`), so an
    // empty leg pick falls back to the root pick on both sides and a No grades the same.
    pick: leg.pick || bet.pick || null,
    trackingStatus: leg.trackingStatus ?? bet.trackingStatus ?? null,
    // #1353 PR 3b: the DNP flag rides to the root so calcStatus sees it on a single.
    frozenDnp: leg.frozenDnp === true ? true : null,
    // #1119 A2-D6: the first-goal team (server lift or cron liveContext), only when
    // the leg is on the graded game -- the server's gradedFirstTeamToScore rule
    // (PR-B D5); the team check itself lives in calcStatus.
    firstTeamToScore: sameGameId(leg.gameId || bet.gameId, game?.id) ? legFirstTeamToScore(leg) : null,
    // The hydrated single grades only on the lifted value: a root-level
    // liveContext must never bypass the same-game rule above.
    liveContext: undefined,
  };
}

/**
 * hydrateLiveParlayLegs -- display-only live stat for a multi's legs (#1669).
 *
 * Single job: give each leg whose OWN game (leg.gameId, as rawParlayLegStatuses resolves
 * it) is present and pre-terminal a `liveStatVal` of `frozenStat ?? statValue`, so a live
 * parlay grades winning/losing instead of stalling at PENDING. Same rule as
 * hydrateSingleLeg, per leg. A terminal or missing game is left alone, so a missed freeze
 * still reads `frozenStat` only and matches the settlement processor.
 *
 * Reads bet.legs and games; returns a copy, never mutates. Only betDisplayStatus calls it:
 * parlayStatus / rawParlayLegStatuses are imported by api/lib/settlement-processor.js and
 * stay unchanged (CC-DENY-012).
 */
function hydrateLiveParlayLegs(bet, games) {
  if (!bet.legs?.length) return bet;
  let changed = false;
  const legs = bet.legs.map((leg) => {
    if (leg?.statValue === null || leg?.statValue === undefined) return leg;
    const legGame = (games || []).find((g) => String(g.id) === String(leg.gameId || ""));
    if (!legGame || legGame.status === "FINAL" || legGame.status === "OFF") return leg;
    changed = true;
    return { ...leg, liveStatVal: leg.frozenStat ?? leg.statValue };
  });
  return changed ? { ...bet, legs } : bet;
}

function sameGameId(a, b) {
  const x = String(a ?? ""), y = String(b ?? "");
  return x !== "" && x === y;
}

// #817: RAW per-leg grade for a parlay/SGP. Opts into push (allowPush=true) so an
// exact-line team/O-U leg returns a literal `push` — the DURABLE representation
// persisted to bet_legs.leg_status by the settlement processor (raw output).
// parlayLegStatuses() wraps this and maps push→void for parent aggregation; the
// processor's RPC payload consumes THIS raw output directly (never the mapped
// void). Ratified §0.5(1,3): a pushed parlay leg voids OUT of the win/lose
// recompute but is stored as `push`, not `void`. FTTS has no push branch (§0.5.5)
// so allowPush=true is inert for FTTS legs — they still grade won/lost only.
/**
 * gradingLeague -- the ONE league a client grading call may use for a leg, or null.
 *
 * Single job: apply the shared league authority (src/lib/league-authority.js, also
 * used by the settlement cron) to the signals the client has -- bets.league (root),
 * leg.league, and the resolved game's league -- and return the single agreed league.
 * Any disagreement, or no known signal at all, returns null, which calcStatus grades
 * `unresolvable` and isSettlementPendingForBet treats as not pending. Never defaults.
 *
 * Replaces every `leg.league ?? bet.league` / `b.league ?? 'nhl'` choice (#1037
 * round 3 P1): a normalized bet_legs row saying 'nhl' under an 'nfl' root, or on an
 * nfl_* game, chose the NHL grading path before this existed.
 */
export function gradingLeague(bet, leg, game) {
  // The leg SIGNAL is the raw source league rowToBet preserved (bet_legs.league, or the
  // client-authored JSONB value) -- `leg.league` itself is root-stamped for renderers
  // and would make every leg look consistent (Security r4 P2-1).
  const legSignal = leg ? (leg.sourceLeague !== undefined ? leg.sourceLeague : leg.league) : undefined;
  // A game's league alone cannot grant authority -- it says what the GAME is, not
  // what the BET is. The server never grades a leg with no league (missing_leg_league);
  // the client mirrors that: no root and no leg signal -> null, whatever the game says.
  // The null-ROOT policy lives at the client read boundary (rowToBet withholds
  // sourceLeague under a null root), not here -- this helper also runs on the server
  // via rawParlayLegStatuses, where a null-root bet that passed the authority check
  // must still grade (#610).
  if (normalizeLeagueSignal(bet?.league) === null && normalizeLeagueSignal(legSignal) === null) return null;
  // An EXPLICIT empty/whitespace leg league is a present-but-invalid signal, not an
  // absent one: the server skips such a leg as missing_leg_league and never grades it
  // (settlement-processor `!l.league`), so the client must not fall through to the
  // root and fabricate a verdict (SlipTicket #818 parity test). An absent field still
  // defers to the root -- rowToBet stamps every real leg, so that is fixture territory.
  if (leg && typeof legSignal === 'string' && normalizeLeagueSignal(legSignal) === null) return null;
  const r = resolveLeagueAuthority({
    root: bet?.league,
    legLeagues: leg ? [legSignal] : [],
    gameLeagues: game ? [game.league] : [],
    known: SUPPORTED_LEAGUES,
  });
  return r.status === 'ok' ? r.league : null;
}

/**
 * isNotGradedLeague -- is this a league the app tracks but nothing can grade yet?
 *
 * Single job (#1655 B3b, A2): true iff the normalised league is in SUPPORTED_LEAGUES and
 * not in SETTLEABLE_LEAGUES. Callers always pass gradingLeague(bet, leg, game), the same
 * input isSettlementPendingForBet uses, so NOT GRADED and SETTLING cannot both hold. A
 * null, 'unknown', corrupt or mismatched league is false (it stays MISSING DATA). Display
 * only: no api/ path reads it. Inert until B3c puts a non-settleable league in SUPPORTED.
 */
export function isNotGradedLeague(league) {
  const key = normalizeLeagueSignal(league);
  return key !== null && SUPPORTED_LEAGUES.includes(key) && !SETTLEABLE_LEAGUES.includes(key);
}

/**
 * notGradedLegGameId -- the game a leg's NOT GRADED verdict may read, or null.
 *
 * Single job (#1655 B3b pre-Codex P2): the leg's own gameId; the root bets.game_id only for an
 * SGP (one game by construction, P3-7) or a single's only leg. import-confirm writes
 * bets.game_id = legs[0].gameId for EVERY slip type, so a parlay (or legacy sgp+) leg with no
 * game must never borrow leg 0's game -- an unplayed or unmatched leg would read finished.
 * Shared by isNotGradedLegOnFinal and the not-graded games fetch so the two cannot drift.
 */
export function notGradedLegGameId(bet, leg) {
  if (leg?.gameId) return leg.gameId;
  const t = bet?.slipType;
  if (t === 'sgp') return bet?.gameId || null;
  if (t === 'parlay' || t === 'sgp+') return null;
  return (bet?.legs?.length ?? 0) <= 1 ? (bet?.gameId || null) : null;
}

/**
 * isNotGradedLegOnFinal -- is this tracked leg in a not-graded league, on a FINAL/OFF game?
 *
 * Single job: the per-leg half of the not_graded display rule. The leg's game comes from
 * notGradedLegGameId; a leg with no game, or an untracked leg (P3-2), is false.
 */
export function isNotGradedLegOnFinal(bet, leg, games) {
  if (isUntrackedLeg(leg)) return false;
  const gid = notGradedLegGameId(bet, leg);
  if (!gid) return false;
  const g = (games || []).find((x) => String(x.id) === String(gid));
  if (!isFinalGame(g)) return false;
  return isNotGradedLeague(gradingLeague(bet, leg, g));
}

/**
 * betDisplayStatus -- the status a page shows for a bet (Dashboard, Results).
 *
 * Single job: finalResult if the cron has spoken; otherwise the client recompute
 * (parlayStatus for multis, calcStatus for singles) with the league chosen by
 * gradingLeague -- never a hard-coded default. Extracted from DashboardPage.jsx and
 * ResultsPage.jsx, which both defaulted `b.league ?? 'nhl'` and so graded a null-root
 * single as NHL while SlipTicket showed it unresolvable (Ultron finding 2).
 *
 * #1655 B3b: an unsettled bet whose every leg is a tracked not-graded-league leg on a
 * FINAL/OFF game reads 'not_graded' (display only: calcStatus / parlayStatus never return
 * it, it is never persisted, and the settlement processor never calls this function).
 */
export function betDisplayStatus(bet, games, { isMulti, trustDnpFlag } = {}) {
  if (!bet) return 'pending';
  if (bet.finalResult) return bet.finalResult;
  // Callers with their own multi definition (SlipTicket: parlay|sgp only) pass it so
  // this consolidation changes nothing about how they render a legacy 'sgp+' record.
  if (isMulti === undefined) isMulti = bet.slipType === 'parlay' || bet.slipType === 'sgp' || bet.slipType === 'sgp+';
  const opts = { trustDnpFlag: trustDnpFlag === true };
  if (isMulti) {
    const hydrated = hydrateLiveParlayLegs(bet, games);
    const s = parlayStatus(hydrated, games, opts);
    // #1655 B3b: a lost leg still says LOST (defensive -- a not-graded leg never grades).
    if (s !== 'lost' && bet.legs?.length > 0 && bet.legs.every((leg) => isNotGradedLegOnFinal(bet, leg, games))) {
      return 'not_graded';
    }
    // #1669: parlayStatus never aggregates `close`, so a live slip whose progressing legs
    // are all one short would still read PENDING. Display only -- the processor keeps
    // calling parlayStatus directly.
    // A leg that cannot be graded (a missed freeze) keeps the slip pending.
    if (s !== 'pending') return s;
    const legs = parlayLegStatuses(hydrated, games, opts);
    return legs.includes('close') && !legs.includes('unresolvable') ? 'close' : s;
  }
  const g = (games || []).find((x) => String(x.id) === String(bet.gameId));
  const leg0 = bet.legs?.[0] ?? null;
  // #1655 B3b: before the untracked path (it needs a tracked leg, so they never overlap).
  if (isNotGradedLegOnFinal(bet, leg0, games)) return 'not_graded';
  if (isUntrackedLeg(leg0)) {
    const gid = leg0.gameId || bet.gameId;
    if (!gid) return 'unresolvable';
    const lg = (games || []).find((x) => String(x.id) === String(gid));
    return (lg?.status === 'FINAL' || lg?.status === 'OFF') ? 'unresolvable' : 'pending';
  }
  return calcStatus(hydrateSingleLeg(bet, g), g, gradingLeague(bet, leg0, g), false, opts);
}

export function rawParlayLegStatuses(bet, games, opts = {}) {
  if (!bet.legs?.length) return [];
  return bet.legs.map(leg => {
    if (isUntrackedLeg(leg)) return 'pending';
    const legGame = games.find(g => String(g.id) === String(leg.gameId || ""));
    const legGameFinal = legGame?.status === "FINAL" || legGame?.status === "OFF";
    // #791 client-descope: a missed-freeze ENRICHABLE PLAYER PROP (frozenStat
    // null on a FINAL game) is unresolvable client-side — grade `unresolvable`
    // (parlayStatus keeps the slip `pending`: isTerminallyUngradeableLeg excludes an
    // unfrozen leg that can still be frozen, #1138), never a false void/WON.
    // Scoped to enrichable props so team legs fall through to calcStatus and
    // grade normally; strict `=== null` so server-shaped legs (frozenStat
    // undefined via betLegToLegShape) are unaffected.
    const league = gradingLeague(bet, leg, legGame);
    if (isEnrichablePlayerProp(marketKey(leg), league)
        && leg.frozenStat === null && legGameFinal && leg.frozenDnp !== true) return "unresolvable";
    return calcStatus(
      {...leg, liveStatVal: leg.frozenStat ?? leg.liveStatVal ?? null},
      legGame || null,
      league,
      true, // #817: allowPush — exact-line parlay/SGP leg grades raw `push`
      opts
    );
  });
}

// #817: MAPPED per-leg grade for parent aggregation. Wraps rawParlayLegStatuses
// and translates every raw `push` → `void` (Option A, §0.5.2) so parlayStatus's
// existing `nonVoid` filter recomputes on surviving legs untouched. Never used
// for the DB payload — the processor persists the raw output above.
export function parlayLegStatuses(bet, games, opts = {}) {
  return rawParlayLegStatuses(bet, games, opts).map(s => (s === "push" ? "void" : s));
}

export function parlayStatus(bet, games, opts = {}) {
  if (!bet.legs?.length) return "pending";
  const statuses = parlayLegStatuses(bet, games, opts);
  const nonVoid = statuses.filter(s => s !== "void");
  if (!nonVoid.length) return "push";
  if (nonVoid.some(s=>s==="lost")) return "lost";
  // Project A: with an untracked leg the slip can never be 'won' until backfill. Once
  // every TRACKED leg is complete it is 'unresolvable' (display only -- the processor
  // refuses before any write). A tracked leg is complete when it graded won/void OR its
  // matching game is FINAL/OFF, whatever its computed status (Codex R1/R2: a final leg
  // can still compute 'unresolvable' or 'pending', e.g. ftts with no signal). A tracked
  // leg with no game is NOT complete (enrichment may still attach one). A lost tracked
  // leg already returned 'lost' above. With no tracked legs at all, the untracked legs'
  // own games decide (no game, or FINAL/OFF).
  if (bet.legs.some(isUntrackedLeg)) {
    const gameOf = (l) => (l.gameId ? (games || []).find(x => String(x.id) === String(l.gameId)) : null);
    const isFinal = (g) => g?.status === "FINAL" || g?.status === "OFF";
    const trackedIdx = bet.legs.map((l, i) => (isUntrackedLeg(l) ? -1 : i)).filter(i => i >= 0);
    const done = trackedIdx.length
      ? trackedIdx.every(i => statuses[i] === "won" || statuses[i] === "void" || isFinal(gameOf(bet.legs[i])))
      : bet.legs.every(l => !l.gameId || isFinal(gameOf(l)));
    if (done) return "unresolvable";
  }
  if (nonVoid.every(s=>s==="won")) return "won";
  if (nonVoid.some(s=>s==="losing")) return "losing";
  // #1138 (Nicky 2026-10-17, CC-DENY-012): a FINAL multi whose gradeable legs all won and
  // whose `unresolvable` legs are STATICALLY ungradeable is terminal `unresolvable` -- the
  // settlement processor writes it through settle_cron_bet instead of skipping it
  // `non_terminal` forever. Every leg's OWN game (leg.gameId only, as rawParlayLegStatuses
  // resolves it -- never bet.gameId, P2-5) must be present and FINAL/OFF, void legs
  // included: a client-trusted DNP on a LIVE game must not make a slip terminal the
  // server holds (P2-3). `lost` already returned above, so a lost leg still decides.
  // Anything that can still change (a pending freeze, FTTS with no first goal, a leg with
  // no game) fails a clause and falls through to `pending` below.
  {
    const legGames = bet.legs.map(l => (games || []).find(g => String(g.id) === String(l.gameId || "")) || null);
    if (legGames.every(isFinalGame)
        && nonVoid.every(s => s === "won" || s === "unresolvable")
        && nonVoid.some(s => s === "unresolvable")
        && bet.legs.every((leg, i) => statuses[i] !== "unresolvable"
          || isTerminallyUngradeableLeg(leg, statuses[i], legGames[i], gradingLeague(bet, leg, legGames[i])))) {
      return "unresolvable";
    }
  }
  if (nonVoid.some(s => s === "unresolvable")) return "pending";
  if (nonVoid.some(s=>s==="winning"||s==="won")) return "winning";
  return "pending";
}

// #878 Phase 1 — VOID-AWARE display payout. When a settled parlay/SGP leg pushes
// (voids out via parlayLegStatuses), the stored book-sourced combinedOdds still
// embeds that leg's odds → over-stated payout. This recomputes effective odds on
// the SURVIVING legs, but ONLY where the math is provably correct; elsewhere it
// refuses to fabricate (INV-1 never over-state, INV-2 never fabricate). Ratified
// spec: docs/specs/2026-08-06-878-parlay-payout-void-recompute.md.
//
// Returns { odds, mode }:
//   'combined'      — no leg voided; odds = combinedOdds unchanged (INV-4).
//   'recomputed'    — independent-leg parlay, ≥1 leg voided, ALL legs carry numeric
//                     odds, and their product reconciles to combinedOdds → the
//                     surviving-legs product is returned.
//   'indeterminate' — a leg voided but recompute is not provably correct: SGP/sgp+
//                     (correlated odds, not a product — INV-3), any null leg odds, or
//                     a combinedOdds that does not reconcile to the leg product (e.g.
//                     a boost/promo). odds=null; callers show '–' and EXCLUDE the bet
//                     from settled aggregate sums (INV-6) — never zero, never estimate.
//   'push'          — every leg voided → parlayStatus is 'push' (full refund). odds=null.
// Display-only: combinedOdds stays import-owned; nothing is written. Singles never
// call this (no legs to void); active/unsettled bets have no void legs → 'combined'.
export const PARLAY_ODDS_RECONCILE_TOLERANCE = 0.02; // 2% — slack for book American-odds rounding

export function effectiveParlayOdds(bet, games, opts = {}) {
  const combined = bet?.combinedOdds ?? null;
  const legs = bet?.legs || [];

  // Fail-closed: a multi with no legs is malformed → indeterminate (never combined).
  if (!legs.length) return { odds: null, mode: "indeterminate" };

  // Void membership prefers the DURABLE persisted leg status (settlement-owned,
  // #944): a settled pushed leg is `push`/`void` regardless of whether its
  // historical game is still in the client `games` array. Only fall back to a
  // recompute when a durable per-leg status is unavailable (e.g. pre-#944 rows).
  // This is load-bearing for INV-1 — a missing game must not restore full odds.
  const recomputed = parlayLegStatuses(bet, games, opts); // raw push already mapped → void
  const legVoided = legs.map((leg, i) => {
    const ds = leg?.status;
    if (ds === "push" || ds === "void") return true;
    if (ds === "won" || ds === "lost") return false; // durable terminal, not a void
    return recomputed[i] === "void";                 // no durable status → recompute
  });
  const voidedCount = legVoided.reduce((n, v) => n + (v ? 1 : 0), 0);

  // No voided leg → the bet paid exactly as priced; combinedOdds is correct for
  // ANY slip type incl. SGP/boosted (nothing to recompute) — byte-identical (INV-4).
  if (voidedCount === 0) return { odds: combined, mode: "combined" };

  const survivors = legs.filter((_, i) => !legVoided[i]);
  // Every leg voided → parlay push (full stake refund); no winning multiplier.
  if (survivors.length === 0) return { odds: null, mode: "push" };

  // A leg voided → recompute is valid ONLY for independent-leg parlays. SGP/sgp+
  // odds are correlated — combinedOdds is not the leg product — so a pushed leg
  // cannot be divided back out client-side (INV-3). Book-sourced payout is Phase 2.
  if (bet?.slipType !== "parlay") return { odds: null, mode: "indeterminate" };

  // Every leg must carry a REAL numeric odds value. Note Number(null)/Number("") are
  // 0 (finite), so an explicit presence + non-zero check is required — otherwise a
  // missing leg-odds is silently treated as a 1.0 multiplier and fabricates a result.
  const hasNumericOdds = l => {
    const o = l?.odds;
    return o !== null && o !== undefined && o !== "" &&
      Number.isFinite(Number(o)) && Number(o) !== 0;
  };
  if (!legs.every(hasNumericOdds)) return { odds: null, mode: "indeterminate" };

  // combinedOdds must reconcile to the FULL leg product — proves it is a plain
  // product (no boost/promo/correlation) before we drop a leg from the multiplier.
  const combinedDec = combined != null ? toDecimal(combined) : NaN;
  const productAll = legs.reduce((acc, l) => acc * toDecimal(l.odds), 1);
  const reconciles = Number.isFinite(combinedDec) && combinedDec > 1 &&
    Math.abs(productAll - combinedDec) / combinedDec <= PARLAY_ODDS_RECONCILE_TOLERANCE;
  if (!reconciles) return { odds: null, mode: "indeterminate" };

  // All guards pass → recompute on the surviving legs only.
  const productSurv = survivors.reduce((acc, l) => acc * toDecimal(l.odds), 1);
  return { odds: toAmerican(productSurv), mode: "recomputed" };
}

// #878 Phase 1 — the dollar payout a SETTLED WON bet contributes to a P&L sum,
// void-aware. Returns a Number payout, or `null` when the bet is INDETERMINATE and
// must be EXCLUDED from the sum (INV-6) — the caller drops it, never zeroing or
// estimating it. Singles pay at bet.odds; parlays/SGP recompute on surviving legs
// where provable (effectiveParlayOdds), else exclude. Shared by ResultsPage
// (net / per-sportsbook / per-type) and DashboardPage (wonAmt) so the exclusion
// rule lives in exactly one place.
export function settledWonPayout(bet, games, opts = {}) {
  const stake = Number(bet?.stake) || 0;
  if (bet?.slipType === "single") return Number(calcPayout(stake, bet?.odds));
  const { odds, mode } = effectiveParlayOdds(bet, games, opts);
  // Indeterminate OR an all-push parent contributes NOTHING to a won-sum — exclude it,
  // never fall back to the full combinedOdds (INV-6; a won+all-push is bad data, not a win).
  if (mode === "indeterminate" || mode === "push") return null;
  // combined mode → odds IS bet.combinedOdds; recomputed → the surviving-legs product.
  // #878 FC-2: a null/empty odds (missing combinedOdds) EXCLUDES — never fabricate +100.
  if (odds == null || odds === "") return null;
  return Number(calcPayout(stake, odds));
}

// --- SLIP CLASSIFICATION ------------------------------------------------------
export function classifySlip(slip) {
  if (!slip.legs || slip.legs.length === 0) return slip;
  const missingLegs = slip.legs.filter(l => l.gameId == null || l.gameId === "");
  const flaggedLegs = missingLegs.length > 0
    ? slip.legs.map(l =>
        (l.gameId == null || l.gameId === "") ? {...l, gameIdMissing: true} : l
      )
    : slip.legs;
  if (missingLegs.length > 0) {
    return {...slip, legs: flaggedLegs, hasUnmatchedLegs: true};
  }
  // Single-leg bets: preserve server-assigned slipType (e.g. 'single')
  if (slip.legs.length <= 1) {
    return {...slip, legs: flaggedLegs};
  }
  const distinctGameIds = [...new Set(slip.legs.map(l => String(l.gameId)))];
  const derivedSlipType = distinctGameIds.length === 1 ? "sgp" : "parlay";
  return {...slip, slipType: derivedSlipType, legs: flaggedLegs};
}

// --- BET STATUS STYLE MAP -----------------------------------------------------
export const SM = {
  won:    {color:"#4ade80",dim:"#052e16",label:"WON",     icon:"✓"},
  winning:{color:"#4ade80",dim:"#052e16",label:"WINNING", icon:"↑"},
  close:  {color:"#fb923c",dim:"#1c0a00",label:"IN PLAY", icon:"→"},
  lost:   {color:"#f87171",dim:"#450a0a",label:"LOST",    icon:"✗"},
  losing: {color:"#f87171",dim:"#1c0101",label:"LOSING",  icon:"↓"},
  // Signal Scarcity (PR 3, D10): pending is the neutral identity shared with JT.status.pending
  // (secondary text on the tile surface) -- not the VOID/PUSH grey, which was 3.7:1 at 11px.
  pending:{color:"#94a3b8",dim:"#0b1326",label:"PENDING", icon:"·"},
  void:   {color:"#64748b",dim:"#0f172a",label:"VOID",    icon:"–"},
  push:         {color:"#64748b",dim:"#0f172a",label:"PUSH",          icon:"–"},
  unresolvable: {color:"#fbbf24",dim:"#1c1300",label:"MISSING DATA",  icon:"?"},
  // #1655 B3b: display-only (betDisplayStatus), never from calcStatus/parlayStatus, never
  // persisted. The neutral pending identity (Signal Scarcity: no accent, no new hex).
  not_graded:   {color:"#94a3b8",dim:"#0b1326",label:"NOT GRADED",    icon:"–"},
};
/**
 * Is this bet waiting on the settlement cron to grade it?
 *
 * UI-only derived state, extracted from SlipTicket so it can be asserted directly.
 * Drives the "SETTLING" pill and the "will move this to Results once graded" banner.
 *
 * The settleability check is the #1037 fix (GPT audit P1-2). Previously this asked
 * only "has the game ended and is the bet ungraded?", which is permanently true for a
 * league with no settlement path -- NFL sat in SETTLING forever, promising a result
 * nothing could deliver. Worse, the pill OVERRIDES visual state (Hotspot #10a), so it
 * masked the `unresolvable` presentation the card derives correctly on its own.
 *
 * FAILS CLOSED on an unknown league: no promise without proof something can settle it.
 * A multi-leg bet needs EVERY leg settleable -- one leg that can never grade means the
 * parlay can never be graded either.
 *
 * (#1040 PR C) a leg on a market the league's `GRADABLE_MARKETS_BY_LEAGUE` entry does
 * not admit can never grade either -- the processor refuses it every cycle by design --
 * so it is not settlement-pending; a null market is admitted (G13, it grades terminal
 * `unresolvable`).
 */
export function isSettlementPendingForBet({ bet, game, games, isMulti }) {
  if (!bet) return false;
  if (bet.settledAt || bet.finalResult) return false;
  // Project A: the processor refuses a bet with an untracked leg (expected skip), so it
  // is never "settling" -- otherwise a NULL market reads as admitted below and the
  // ticket shows settling forever (hotspot 10a).
  if (bet.legs?.some(isUntrackedLeg)) return false;

  const settleable = (league) =>
    league != null && SETTLEABLE_LEAGUES.includes(String(league).trim().toLowerCase());

  // Mirrors calcStatus's market admission (betting.js ~:218-221): a league with no
  // GRADABLE_MARKETS_BY_LEAGUE entry is ungated (NHL/MLB unchanged); a league with an
  // entry only promises settlement for a member market. A missing/empty market (G13)
  // is admitted -- it grades terminal `unresolvable`, never refused by the processor.
  const admitted = (league, legOrBet) => {
    const key = String(league).trim().toLowerCase();
    const gradable = GRADABLE_MARKETS_BY_LEAGUE[key];
    if (!gradable) return true;
    const market = marketKey(legOrBet);
    return !market || gradable.has(market);
  };

  if (isMulti) {
    if (!(bet.legs?.length > 0)) return false;
    return bet.legs.every((leg) => {
      // #1782: the leg's own game only, as the processor's gameOfLeg grades it. import-confirm
      // writes bets.game_id = legs[0].gameId for every slip type, so a leg not enriched yet
      // must not borrow leg 0's game: the cron refuses that multi (unresolved_game_id_missing).
      const g = leg.gameId ? games?.find((x) => String(x.id) === String(leg.gameId)) : null;
      // The shared league authority (root / leg / resolved game must agree) -- a
      // disagreement is null and therefore not settleable. rowToBet already stamps the
      // root onto every leg; this is the second layer and it also sees the game.
      const league = gradingLeague(bet, leg, g);
      if (!(settleable(league) && admitted(league, leg))) return false;
      return Boolean(g) && (g.status === 'FINAL' || g.status === 'OFF');
    });
  }

  // The single's leg carries the sourceLeague signal too -- pass it, not null.
  const league = gradingLeague(bet, bet.legs?.[0] ?? null, game);
  if (!(settleable(league) && admitted(league, bet.legs?.[0] ?? bet))) return false;
  return Boolean(game) && (game.status === 'FINAL' || game.status === 'OFF');
}
